import { FC, useState, useEffect } from 'react';
import { translate } from 'translations';
import { AutocompleteWrapper, Button, Icon, Input, LearningPanelNotification } from 'learning-panel-components';
import styles from './contactSupport.module.scss';

const ContactSupport: FC<{
    onSubmit: Function;
    isFormSent: boolean;
    isLoading: boolean;
    onClose: Function;
    defaultValues?: { subject: string; description: string };
    isUpgrade?: boolean;
}> = ({
    onSubmit,
    isFormSent,
    isLoading,
    onClose,
    defaultValues,
    isUpgrade
}: {
    onSubmit: Function;
    isFormSent: boolean;
    isLoading: boolean;
    onClose: Function;
    defaultValues?: { subject: string; description: string };
    isUpgrade?: boolean;
}) => {
    const [showSuccessNotification, setShowSuccessNotification] = useState<boolean>(false);
    const [urgency, setUrgency] = useState<string>('low');
    const [subject, setSubject] = useState<string>(defaultValues?.subject || '');
    const [description, setDescription] = useState<string>(defaultValues?.description || '');

    const urgencyList = [
        { displayName: translate('Low'), uuid: 'low' },
        { displayName: translate('Med'), uuid: 'med' },
        { displayName: translate('High'), uuid: 'high' }
    ];

    const clearForm = () => {
        setSubject('');
        setDescription('');
    };

    useEffect(() => {
        const timer = setTimeout(() => setShowSuccessNotification(false), 50000);
        return () => clearTimeout(timer);
    }, [showSuccessNotification]);

    useEffect(() => {
        if (isFormSent) setShowSuccessNotification(true);
    }, [isFormSent]);

    return (
        <div className={styles['background-wrapper']}>
            <div
                className={styles['contact-support-wrapper']}
                style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/accountSettings.svg)` }}
            >
                <div className={styles['top-wrapper']}>
                    <div className={styles.title}>
                        <div>{translate(isUpgrade ? 'ContactUs' : 'ContactSupport')}</div>
                    </div>
                    <div className={styles['close-wrapper']}>
                        <Icon
                            name="cancel"
                            onClick={() => {
                                clearForm();
                                onClose();
                            }}
                            className={styles.cancel}
                        />
                    </div>
                </div>
                <div className={styles['bottom-wrapper']}>
                    <div className={styles.section}>
                        <div className={styles['up-title']}>
                            <div>{translate('ContactSupportSubject')}</div>
                        </div>
                        <Input
                            inputText={subject}
                            onChange={setSubject}
                            multiline={false}
                            numberOfLines={1}
                            placeHolderText={translate('ContactSupportSubjectPlaceHolder')}
                            maxLength={70}
                        />
                    </div>
                    <div className={styles.section}>
                        <div className={styles['up-title']}>
                            <div>{translate('ContactSupportDescription')}</div>
                        </div>
                        <Input
                            inputText={description}
                            onChange={setDescription}
                            multiline={true}
                            numberOfLines={8}
                            placeHolderText={translate('ContactSupportDescriptionPlaceHolder')}
                            maxLength={400}
                        />
                    </div>
                    {!isUpgrade && (
                        <div className={styles.section}>
                            <div className={styles['up-title']}>
                                <div>{translate('Urgency')}</div>
                            </div>
                            <AutocompleteWrapper
                                list={urgencyList}
                                selectedUuid={urgency}
                                setSelectedUuid={setUrgency}
                                padding="0"
                            />
                        </div>
                    )}
                    <div className={styles['send-button']}>
                        <Button
                            id="send-form-button"
                            type={
                                !isLoading && Boolean(subject) && Boolean(description)
                                    ? 'bigGradient'
                                    : 'bigGradientDisable'
                            }
                            onClick={
                                !isLoading && Boolean(subject) && Boolean(description)
                                    ? () => onSubmit(subject, description, urgency)
                                    : undefined
                            }
                            text={translate('SendForm')}
                            isLoading={isLoading}
                            lowerCase
                        />
                    </div>
                </div>
            </div>
            <div className={styles['notification-popup']}>
                {showSuccessNotification && (
                    <LearningPanelNotification
                        notificationText={translate('FormSent')}
                        variant="almostTransparent"
                        fullWidth
                        handleClose={() => setShowSuccessNotification(false)}
                    />
                )}
            </div>
        </div>
    );
};

export default ContactSupport;
